<template>
  <MultiSelect
    v-model="selectedStatuses"
    :options="statusOptions"
    option-label="label"
    track-by="value"
    :placeholder="placeholder"
    :disabled="loading || disabled"
    :show-tags="showTags"
    :max-count="maxCount"
    :class="containerClass"
    :style="{ width: width, maxWidth: maxWidth }"
    :enable-refresh="enableRefresh"
    :refresh-loading="refreshLoading"
    :modal-popover="true"
    :show-select-all="false"
    :disabled-options="disabledOptions"
    :as-child="enableFilterIcon"
    @update:model-value="handleSelectionChange"
    @refresh="$emit('refresh')">
    
    <template v-if="enableFilterIcon">
      <Button
        variant="outline"
        :disabled="loading || disabled"
        :style="{ width: width, maxWidth: maxWidth }"
        class="h-9 inline-flex w-full justify-center items-center px-3 text-sm font-medium">
        <Filter
          :class="['h-4 w-4 mr-1', hasSelection ? 'text-primary' : '']"
          :fill="hasSelection ? 'currentColor' : 'none'" />
        <span class="px-1">
          {{ hasSelection ? `${selectedStatuses.length} selected` : placeholder }}
        </span>
        <ChevronDown class="-mr-1 ml-2 h-5 w-5" />
      </Button>
    </template>
  </MultiSelect>
</template>

<script setup>
import { computed } from 'vue'
import { MultiSelect } from '@/components/ui/multi-select'
import { Button } from '@/components/ui/button'
import { Filter, ChevronDown } from 'lucide-vue-next'

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => []
    },
    statuses: {
        type: Array,
        default: () => ['Active', 'Broken', 'Delayed', 'Incomplete', 'Paused']
    },
    includeAll: {
        type: Boolean,
        default: true
    },
    allLabel: {
        type: String,
        default: 'All Statuses'
    },
    placeholder: {
        type: String,
        default: 'Select statuses...'
    },
    loading: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    showTags: {
        type: Boolean,
        default: true
    },
    maxCount: {
        type: Number,
        default: 2
    },
    width: {
        type: String,
        default: 'auto'
    },
    maxWidth: {
        type: String,
        default: '12rem'
    },
    containerClass: {
        type: String,
        default: 'min-w-32'
    },
    enableRefresh: {
        type: Boolean,
        default: false
    },
    refreshLoading: {
        type: Boolean,
        default: false
    },
    enableFilterIcon: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:modelValue', 'refresh'])

const selectedStatuses = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
})

const hasSelection = computed(() => {
    return props.modelValue && props.modelValue.length > 0 && !props.modelValue.includes('All')
})

const statusOptions = computed(() => {
    const options = []
  
    // Add "All" option if enabled
    if (props.includeAll) {
        options.push({
            value: 'All',
            label: props.allLabel
        })
    }
  
    // Add status options
    const statusOpts = props.statuses.map(status =>
        typeof status === 'string'
            ? { value: status, label: status }
            : status
    )
  
    options.push(...statusOpts)
    return options
})

const disabledOptions = computed(() => {
    const hasAll = props.modelValue.includes('All')
  
    if (hasAll) {
    // If "All" is selected, disable all other options
        return statusOptions.value
            .filter(option => option.value !== 'All')
            .map(option => option.value)
    }
  
    return []
})

const handleSelectionChange = (newSelection) => {
    const hasAll = newSelection.includes('All')
    const hasOthers = newSelection.some(item => item !== 'All')
  
    let finalSelection
  
    if (hasAll && hasOthers) {
    // If "All" is selected along with others, keep only "All"
        finalSelection = ['All']
    } else if (hasAll) {
    // If only "All" is selected, keep it
        finalSelection = ['All']
    } else {
    // If no "All", keep the other selections
        finalSelection = newSelection.filter(item => item !== 'All')
    }
  
    emit('update:modelValue', finalSelection)
}
</script>